import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject } from 'rxjs';
import { PreCallInfo } from '@nimbus-assistant/enums/pre-call-info';
import { Language } from '@nimbus-library/features/localization/models/enums/language.enum';
import { LocalizationActions } from '@nimbus-library/features/localization/store/localization.action';
import { WindowPosition } from '../enums/window-position';
import { Language as Languages } from '../enums/language';
import { NimbusUserSettings } from '../models/nimbus-user-settings';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private _userSettings!: NimbusUserSettings;

  public readonly defaultWidth = 320;
  public readonly defaultHeight = 474; // +2 for borders, +12 for grab bar
  public readonly defaultIncreasedHeight = 618; // +2 for borders, +12 for grab bar

  public readonly hostSettingsReceived$ = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly store: Store,
    private readonly loggingService: LoggingService,
  ) {
    if (!this._userSettings) {
      this._userSettings = new NimbusUserSettings({
        autorun: true,
        language: Languages.en,
        position: WindowPosition.BottomRight.toString(),
        openLoginWindowOnStartup: false,
        onTopTeamToast: false,
        preCallInfo: PreCallInfo.CallFlow,
        bringToForeground: true,
      } as NimbusUserSettings);
      this.store.dispatch(new LocalizationActions.SetLanguage(this._userSettings.language as Language));
    }
  }

  public set userSettings(settings: NimbusUserSettings) {
    this.loggingService.Info(`UserSettings: ${JSON.stringify(settings)}`);
    if (settings) {
      this._userSettings.autorun = settings.autorun;
      this._userSettings.language = settings.language;
      this._userSettings.position = settings.position;
      this._userSettings.openLoginWindowOnStartup = settings.openLoginWindowOnStartup;
      this._userSettings.onTopTeamToast = settings.onTopTeamToast;
      this._userSettings.preCallInfo = settings.preCallInfo;
      this._userSettings.zoom = settings.zoom;
      this._userSettings.useAudioForInstantMessages = settings.useAudioForInstantMessages;
      this._userSettings.audioForInstantMessages = settings.audioForInstantMessages;
      this._userSettings.useAudioForExternalTasks = settings.useAudioForExternalTasks;
      this._userSettings.audioForExternalTasks = settings.audioForExternalTasks;
      this._userSettings.useAudioForEmails = settings.useAudioForEmails;
      this._userSettings.audioForEmails = settings.audioForEmails;
      this._userSettings.bringToForeground = settings.bringToForeground;
      this._userSettings.topmost = settings.topmost;
      this._userSettings.signalRLoggingEnabled = settings.signalRLoggingEnabled;
      this._userSettings.windowWidth = this.defaultWidth;
      this._userSettings.windowHeight = this.defaultHeight;
      this._userSettings.windowMarginVertical = settings.windowMarginVertical;
      this._userSettings.windowMarginRight = settings.windowMarginRight;

      this.store.dispatch(new LocalizationActions.SetLanguage(this._userSettings.language as Language));
      // Only a real settings object may open the gate: an empty message would let the startup defaults be written back
      this.hostSettingsReceived$.next(true);
    }
  }

  public get userSettings(): NimbusUserSettings {
    return this._userSettings;
  }
}
